import Button, { SizeType } from '@components/atoms/button'
import { FastlyImage } from '@components/common/image'
import Link from 'next/link'
import type { ReactNode } from 'react'
import { defineMessage, Trans } from '@lingui/macro'
import MsgTrans from '@components/common/i18n/message-trans'
import BookFreeClassButton from '@components/common/book-free-class-btn'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { LinguiJSMessageDescriptor } from '@lib/types/common'

export type StatisticsProps = {
  title: string
  description: LinguiJSMessageDescriptor
}

const STATISTICS: StatisticsProps[] = [
  {
    title: '🎓 1000+',
    description: defineMessage({
      message: 'Graduate CS instructors',
    }),
  },
  {
    title: '👨‍💻 7+',
    description: defineMessage({
      message: 'Years of coding experience',
    }),
  },
  {
    title: '👩 91%',
    description: defineMessage({
      message: 'Female educators',
    }),
  },
  {
    title: '💻 10+',
    description: defineMessage({
      message: 'Programming languages',
    }),
  },
]

type Props = {
  title?: string | ReactNode
  description?: string | ReactNode
  isCampPage?: boolean
  isCoursePage?: boolean
  shouldScrollToTop?: boolean
  isShowBecomeTeacher?: boolean
  ctaSize?: SizeType
  urlQuery?: string
  ctaText?: string
  ctaLink?: string
  statistics?: StatisticsProps[]
  imagePath?: string
}

const defaultTitle = (
  <>
    <Trans id="">
      Learn coding from the{' '}
      <span className="yellow-underline">experienced teachers.</span> Our
      hand-picked coding teachers ensure the best learning experience.
    </Trans>
  </>
)

const Teachers = ({
  title = defaultTitle,
  description,
  isCampPage = false,
  isCoursePage = false,
  shouldScrollToTop,
  isShowBecomeTeacher = true,
  ctaSize = 'xl',
  urlQuery,
  ctaText,
  ctaLink,
  statistics,
  imagePath,
}: Props) => {
  const statisticsContent = statistics || STATISTICS
  return (
    <div className="container mx-auto py-12 lg:py-16 px-5 text-grey">
      {isCoursePage && (
        <h2 className="text-2xl font-600 leading-tight mb-8">{title}</h2>
      )}
      {!isCoursePage && (
        <h2 className="text-center text-2xl lg:text-4xl font-600 max-w-4xl mx-auto mb-4 leading-tight">
          {title}
        </h2>
      )}
      {description && !isCoursePage && !isCampPage && (
        <p className="text-base lg:text-lg text-center text-grey max-w-4xl mx-auto">
          {description}
        </p>
      )}

      <div className="flex flex-col items-center lg:flex-row gap-x-10 justify-center leading-tight">
        <div className="flex-shrink-0">
          <FastlyImage
            src={`${
              imagePath
                ? `${imagePath}.png`
                : '/images/locale-images/us/teachers/teachers-v3.png'
            }`}
            height={426}
            width={471}
            alt="Codingal teachers"
          />
        </div>

        <div className="flex flex-col space-y-6">
          {description && isCampPage && (
            <p className="lg:text-lg text-center lg:text-left">{description}</p>
          )}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 ${
              isCoursePage ? 'xl:grid-cols-4' : ''
            }`}
          >
            {statisticsContent.map((stat, i) => (
              <div
                key={i}
                className="border-orange bg-white border p-3 md:p-5 text-xl flex flex-col rounded-md md:max-w-xs"
              >
                <p className="text-2xl font-700 pb-2">{stat.title}</p>
                <MsgTrans msg={stat.description} />
              </div>
            ))}
          </div>

          {ctaLink && ctaText && (
            <div className="w-full lg:max-w-xs text-center">
              <div className="flex justify-center lg:justify-start">
                {' '}
                <a
                  className="btn btn-orange w-full md:w-auto py-3 text-lg"
                  href={ctaLink}
                >
                  {ctaText}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
      {isCoursePage && (
        <div className="mx-auto w-full md:max-w-xs text-center mt-12">
          <BookFreeClassBtn query={urlQuery} />
        </div>
      )}
      {!isCoursePage && !ctaLink && !ctaText && (
        <div className="flex flex-col lg:flex-row gap-4 mt-12 justify-center mx-auto">
          {isShowBecomeTeacher && (
            <Link href="/teach/" prefetch={false}>
              <a>
                <Button variant="outline" className="w-full" size={ctaSize}>
                  <Trans id="homepage.teacherFold.becomeATeacher">
                    Become a teacher
                  </Trans>
                </Button>
              </a>
            </Link>
          )}
          <BookFreeClassButton
            size={ctaSize}
            shouldScrollToTop={shouldScrollToTop}
            query={urlQuery}
          />
        </div>
      )}
    </div>
  )
}

export default Teachers
